/**
 * The single HTTP client for the whole app.
 *
 * §5.1 forbids scattering `fetch` across pages, so every request goes through
 * here: one place to attach the bearer token, refresh it, decode the Appendix C
 * error envelope, and surface the trace id that makes a support ticket
 * actionable.
 */

const TOKEN_KEY = 'ndm.access_token'
const REFRESH_KEY = 'ndm.refresh_token'

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, unknown>
  trace_id?: string
}

/** Canonical error codes from Appendix C. */
export const ErrorCode = {
  Validation: 'VALIDATION_ERROR',
  Unauthenticated: 'UNAUTHENTICATED',
  Forbidden: 'FORBIDDEN',
  NotFound: 'NOT_FOUND',
  VersionConflict: 'VERSION_CONFLICT',
  RuleTooExpensive: 'RULE_TOO_EXPENSIVE',
  RateLimited: 'RATE_LIMITED',
  QuotaExceeded: 'QUOTA_EXCEEDED',
  Upstream: 'UPSTREAM_ERROR',
  Unavailable: 'TEMPORARILY_UNAVAILABLE',
  Internal: 'INTERNAL_ERROR',
} as const

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>
  readonly traceId?: string
  readonly retryAfter?: number

  constructor(status: number, body: ApiErrorBody, retryAfter?: number) {
    super(body.message || `请求失败（HTTP ${status}）`)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code || ErrorCode.Internal
    this.details = body.details
    this.traceId = body.trace_id
    this.retryAfter = retryAfter
  }

  /** Validation errors carry per-JSONPath messages for the rule builder. */
  get fieldErrors(): Array<{ path: string; message: string }> {
    const raw = this.details?.errors
    return Array.isArray(raw) ? (raw as Array<{ path: string; message: string }>) : []
  }

  /** True when retrying the same request could plausibly succeed. */
  get retryable(): boolean {
    return (
      this.code === ErrorCode.RateLimited ||
      this.code === ErrorCode.Unavailable ||
      this.code === ErrorCode.Upstream ||
      this.status >= 500
    )
  }
}

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh?: string) {
    localStorage.setItem(TOKEN_KEY, access)
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh)
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Idempotency-Key for side-effecting writes (Appendix C.1). */
  idempotencyKey?: string
  /** If-Match version for optimistic locking (§10.1). */
  version?: number
  /** Set false for endpoints that must not attempt a token refresh. */
  allowRefresh?: boolean
}

const BASE = '/api/v1'

/**
 * Refresh is shared: if three queries 401 at once we must not fire three
 * refreshes and invalidate each other's rotated token.
 */
let refreshInFlight: Promise<boolean> | null = null

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refresh = tokenStore.getRefresh()
    if (!refresh) return false
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      })
      if (!res.ok) return false
      const data = await res.json()
      tokenStore.set(data.tokens.access_token, data.tokens.refresh_token)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, idempotencyKey, version, allowRefresh = true, ...init } = opts

  const send = async (): Promise<Response> => {
    const headers = new Headers(init.headers)
    headers.set('Accept', 'application/json')
    if (body !== undefined) headers.set('Content-Type', 'application/json')

    const token = tokenStore.get()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey)
    if (version !== undefined) headers.set('If-Match', String(version))

    return fetch(`${BASE}${path}`, {
      ...init,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  }

  let res = await send()

  // One transparent refresh attempt, then give up and let the caller redirect.
  if (res.status === 401 && allowRefresh && tokenStore.getRefresh()) {
    if (await refreshAccessToken()) {
      res = await send()
    }
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const payload = text ? safeParse(text) : null

  if (!res.ok) {
    const retryAfter = Number(res.headers.get('Retry-After')) || undefined
    const errBody: ApiErrorBody =
      payload && typeof payload === 'object' && 'code' in payload
        ? (payload as ApiErrorBody)
        : { code: ErrorCode.Internal, message: `请求失败（HTTP ${res.status}）` }

    if (res.status === 401) tokenStore.clear()
    throw new ApiError(res.status, errBody, retryAfter)
  }

  return payload as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'DELETE', body }),
}

/**
 * Builds a query string, dropping empty values and expanding arrays into
 * repeated keys (the shape §10.3 specifies for tld[]/status[]/feature[]).
 */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const v of value) if (v !== '' && v != null) search.append(key, String(v))
    } else if (typeof value === 'boolean') {
      search.set(key, value ? 'true' : 'false')
    } else {
      search.set(key, String(value))
    }
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

/** Generates an Idempotency-Key for a write. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/** Triggers a CSV download for endpoints that stream a file. */
export async function download(path: string, body: unknown, filename: string): Promise<void> {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const token = tokenStore.get()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text()
    const payload = safeParse(text)
    throw new ApiError(
      res.status,
      (payload as ApiErrorBody) ?? { code: ErrorCode.Internal, message: '导出失败' },
    )
  }

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
